import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './DermatologyMarketPage.css';

const { Title, Text, Paragraph } = Typography;

const DermatologyMarketPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  // Set page metadata
  useEffect(() => {
    document.title = 'Dermatology Market Intelligence | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Dermatology Pharmaceutical Market Intelligence - Comprehensive market analysis, pipeline tracking, and competitive intelligence for dermatology markets from SNS Intelligence.');
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="dermatology-market-page">
      {/* Navigation */}
      <nav className="dermatology-market-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="dermatology-market-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="dermatology-market-header">
              <Title level={1} className="dermatology-market-title">
                Dermatology Pharmaceutical Market Intelligence
              </Title>
            </div>

            <Card className="dermatology-market-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph className="dermatology-market-intro">
                  The dermatology pharmaceutical market encompasses therapeutic interventions for conditions affecting the skin, hair, and nails. From a commercial and strategic perspective, this market represents a complex landscape characterized by multiple disease areas, diverse treatment modalities, various routes of administration, and evolving competitive dynamics. Market participants must navigate opportunities across inflammatory and autoimmune conditions, infectious diseases, oncologic indications, hair disorders, and other dermatologic areas, each with distinct patient populations, treatment pathways, and competitive landscapes.
                </Paragraph>
                <Paragraph>
                  SNS Intelligence structures the dermatology market to support strategic decision-making. The platform allows professionals to identify where market opportunities align with competitive dynamics, assess how pipeline activity shapes future competitive landscapes, and prioritize resource allocation across heterogeneous sub-markets through comparable analytical frameworks.
                </Paragraph>

                <div>
                  <Title level={2} className="section-title">
                    Market Scope and Coverage
                  </Title>
                  <Paragraph>
                    The dermatology market is inherently fragmented and heterogeneous, with distinct disease areas—such as inflammatory and autoimmune conditions, infectious diseases, oncologic indications, and hair disorders—exhibiting different patient populations, treatment pathways, competitive dynamics, and commercial characteristics. This fragmentation means that aggregate market views can obscure critical strategic insights, making sub-market analysis essential for informed decision-making.
                  </Paragraph>
                  <Paragraph>
                    The platform structures dermatology markets hierarchically, allowing users to compare opportunities across sub-markets while maintaining the granularity needed for strategic assessment. Representative examples include psoriasis, atopic dermatitis, and acne (these examples are not exhaustive). By enabling direct comparison of market dynamics, competitive intensity, and opportunity profiles across sub-markets, the platform supports identification of where strategic focus should be concentrated and where market conditions favor different approaches to development, commercialization, or partnership strategies.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Pipeline and Development Landscape
                  </Title>
                  <Paragraph>
                    Understanding how pipeline activity shapes future competitive dynamics is critical for strategic planning in dermatology. The platform structures pipeline information to reveal where development activity is concentrated versus fragmented, how different developers are positioning across sub-markets, and how emerging therapeutic approaches may reshape competitive landscapes.
                  </Paragraph>
                  <Paragraph>
                    By maintaining linkage between pipeline candidates, developers, and target indications, the platform supports assessment of competitive intensity, identification of whitespace opportunities, and evaluation of partnership or acquisition targets. Users can compare pipeline activity across sub-markets to identify where development crowding may impact commercial prospects, where early-stage activity signals emerging opportunities, and where strategic positioning aligns with market evolution. This analytical capability informs portfolio prioritization, competitive monitoring, and business development activities.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Forecast and Segmentation Analysis
                  </Title>
                  <Paragraph>
                    Multi-dimensional segmentation is critical in dermatology markets because opportunity, competitive dynamics, and access considerations vary significantly across therapeutic categories, delivery routes, drug types, distribution channels, and geographic markets. The platform structures forecasts and market views to support strategic analysis across these dimensions, enabling identification of where market opportunity aligns with competitive positioning and access requirements.
                  </Paragraph>
                  <Paragraph>
                    This segmentation capability supports strategic decision-making by revealing how opportunity profiles differ across dimensions—for example, how prescription versus over-the-counter dynamics affect competitive positioning, how route of administration influences market access strategies, or how geographic variations in treatment patterns create distinct regional opportunities. By enabling analysis across therapeutic categories, drug classes including biologics and small molecules, distribution channels, and geographic layers, the platform supports opportunity sizing, access strategy development, and competitive positioning decisions that account for market heterogeneity.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Competitive and Developer Landscape
                  </Title>
                  <Paragraph>
                    Competitive dynamics in dermatology vary significantly across sub-markets, with some areas characterized by concentrated competition among established players and others exhibiting fragmented development activity or whitespace opportunities. The platform structures competitive information to reveal where developer participation is concentrated versus distributed, how pipeline activity signals future competitive intensity, and where strategic positioning patterns create opportunities or challenges.
                  </Paragraph>
                  <Paragraph>
                    By analyzing developer participation patterns across sub-markets, users can identify areas where competitive crowding may impact commercial prospects, where fragmented development suggests partnership or acquisition opportunities, and where strategic positioning aligns with market evolution. The platform supports assessment of competitive positioning across therapeutic areas, development stages, and geographic markets, enabling both competitive intelligence monitoring and business development activities that account for the heterogeneous competitive landscape within dermatology.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Geographic and Regional Perspectives
                  </Title>
                  <Paragraph>
                    Geographic variations in treatment patterns, competitive dynamics, and market access requirements create distinct regional opportunities and challenges within dermatology markets. The platform structures market information to support geographic analysis and market prioritization, maintaining consistent segmentation and methodology across geographic layers to enable meaningful comparison.
                  </Paragraph>
                  <Paragraph>
                    This geographic comparability supports strategic decision-making by revealing where regional market conditions favor different approaches to development, commercialization, or partnership strategies. Users can evaluate regional opportunities, assess market entry strategies, and analyze geographic variations in competitive dynamics and treatment patterns to inform prioritization of resources and strategic focus across global markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Use Cases in Dermatology Strategy and Planning
                  </Title>
                  <Paragraph>
                    The platform supports multiple professional use cases within dermatology market analysis and strategic planning. Opportunity sizing and prioritization enables evaluation of market potential across sub-markets and geographic regions, supporting resource allocation and strategic focus decisions. Portfolio planning leverages pipeline analysis and market sizing data to inform development prioritization and therapeutic area strategy.
                  </Paragraph>
                  <Paragraph>
                    Competitive intelligence monitoring supports tracking of developer activity, pipeline developments, and market participation trends across dermatologic conditions. Partnership and business development scanning enables identification of collaboration opportunities and evaluation of developer positioning. Market entry and regional expansion analysis utilizes geographic segmentation to assess opportunities and competitive dynamics across regions and countries. Scenario planning leverages segmented views to evaluate different market assumptions and strategic options, supporting robust decision-making under uncertainty.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Access Dermatology Market Intelligence
                  </Title>
                  <Paragraph>
                    SNS Intelligence is a subscription-based platform with controlled access, intended for professional and corporate use. Access is managed through organizational subscriptions, ensuring appropriate use and data security. If you are interested in learning more about how the platform supports dermatology market analysis and strategic decision-making, or to discuss your organization's specific analytical needs, we invite you to request access or contact our team to schedule a platform overview.
                  </Paragraph>
                  <div style={{ marginTop: '24px' }}>
                    <Space size="middle">
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={showModal}
                      >
                        Request Free Trial
                      </Button>
                      <Button 
                        size="large"
                        onClick={() => {
                          navigate('/');
                          // Scroll to pricing section after navigation
                          setTimeout(() => {
                            const pricingSection = document.getElementById('pricing');
                            if (pricingSection) {
                              pricingSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                              // Fallback: try using hash
                              window.location.hash = 'pricing';
                            }
                          }, 300);
                        }}
                      >
                        View Pricing
                      </Button>
                    </Space>
                  </div>
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
                    <Text style={{ color: '#595959', fontSize: '16px' }}>
                      Have questions? Contact us at{' '}
                      <a 
                        href="mailto:info@snsintel.com" 
                        style={{ 
                          color: '#1890ff', 
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        info@snsintel.com
                      </a>
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="dermatology-market-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DermatologyMarketPage;
